import { useState } from "react";
import type { ReactNode } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import LockIcon from "@mui/icons-material/Lock";
import type { ApiError } from "../api/client";
import { MONO } from "../theme";

/** Page header with an optional action cluster on the right. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2, gap: 2 }}>
      <Box>
        <Typography variant="h5">{title}</Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        {actions}
      </Stack>
    </Stack>
  );
}

/**
 * Renders an API failure with its field-level detail.
 *
 * The API returns per-field zod issues for a 400; dropping them and showing only
 * "invalid request body" is how a form becomes unusable — the operator has to
 * guess which of fourteen config fields it meant.
 */
export function ErrorBanner({ error, onRetry }: { error: ApiError | undefined; onRetry?: () => void }) {
  if (!error) return null;
  const fields = error.fieldErrors;
  return (
    <Alert
      severity={error.status === 403 || error.status === 401 ? "warning" : "error"}
      sx={{ mb: 2 }}
      action={
        onRetry ? (
          <Button color="inherit" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      <AlertTitle sx={{ mb: fields.length > 0 ? 0.5 : 0 }}>
        {error.status > 0 ? `${error.status} — ` : ""}
        {error.message}
      </AlertTitle>
      {fields.length > 0 ? (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {fields.map((f, i) => (
            <li key={`${f.path}-${i}`}>
              <Typography variant="body2" component="span" sx={{ fontFamily: MONO }}>
                {f.path || "(body)"}
              </Typography>
              {`: ${f.message}`}
            </li>
          ))}
        </Box>
      ) : null}
    </Alert>
  );
}

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4, color: "text.secondary" }}>
      <CircularProgress size={18} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderStyle: "dashed" }}>
      <Typography variant="body1">{title}</Typography>
      {hint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

/** Colour-coded lead quality, per the meanings documented in docs/ADMIN.md. */
const QUALITY_META: Record<string, { color: "success" | "warning" | "default" | "error"; hint: string }> = {
  ok: { color: "success", hint: "Multi-unit, contactable, domain-verified. Shippable." },
  needs_review: { color: "warning", hint: "Contact matched by company name, not domain. Confirm first." },
  no_contact: { color: "default", hint: "Owner identified, no contact yet. Self-heals on the next enrichment run." },
  low_value: { color: "error", hint: "No multi-unit evidence. Deliberately held back." },
};

export function QualityChip({ quality }: { quality: string }) {
  const meta = QUALITY_META[quality] ?? { color: "default" as const, hint: quality };
  return (
    <Tooltip title={meta.hint}>
      <Chip label={quality} color={meta.color} variant={meta.color === "default" ? "outlined" : "filled"} />
    </Tooltip>
  );
}

export function ActiveChip({ active }: { active: boolean }) {
  return <Chip label={active ? "active" : "paused"} color={active ? "success" : "default"} variant="outlined" />;
}

/**
 * A field whose value the server will not disclose.
 *
 * Leaving it blank means "keep what is stored". Saying so on the control itself
 * matters: an operator who assumes an empty box will clear the credential will
 * avoid touching the form at all.
 */
export function SecretField({
  label,
  value,
  onChange,
  isSet,
  helperText,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  isSet: boolean;
  helperText?: string;
  required?: boolean;
}) {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type="password"
      fullWidth
      required={required && !isSet}
      autoComplete="new-password"
      placeholder={isSet ? "•••••••• stored" : ""}
      InputProps={{
        startAdornment: <LockIcon fontSize="small" sx={{ mr: 1, color: "text.disabled" }} />,
      }}
      helperText={
        helperText ??
        (isSet
          ? "A value is stored and is never sent to the browser. Leave blank to keep it."
          : "Not configured.")
      }
    />
  );
}

/**
 * JSON editor with parse-on-change validation. Config-as-data means the operator
 * genuinely is editing JSON; the least this console can do is refuse to submit a
 * document that will not parse.
 */
export function JsonField({
  label,
  value,
  onChange,
  rows = 12,
  helperText,
}: {
  label: string;
  value: string;
  onChange: (value: string, valid: boolean) => void;
  rows?: number;
  helperText?: string;
}) {
  const [problem, setProblem] = useState<string | null>(null);

  const handle = (next: string) => {
    if (next.trim() === "") {
      setProblem(null);
      onChange(next, false);
      return;
    }
    try {
      JSON.parse(next);
      setProblem(null);
      onChange(next, true);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : "invalid JSON");
      onChange(next, false);
    }
  };

  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => handle(e.target.value)}
      multiline
      minRows={rows}
      fullWidth
      error={problem !== null}
      helperText={problem ?? helperText}
      inputProps={{ style: { fontFamily: MONO, fontSize: 12, whiteSpace: "pre" }, spellCheck: false }}
    />
  );
}

/** Confirmation for a destructive action, naming what is about to happen. */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = "Delete",
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" component="div">
          {body}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={pending}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Wraps an action an operator's role does not permit, explaining why it is off. */
export function RoleGate({
  allowed,
  needs,
  children,
}: {
  allowed: boolean;
  needs: string;
  children: ReactNode;
}) {
  if (allowed) return <>{children}</>;
  return (
    <Tooltip title={`Requires the "${needs}" role`}>
      <Box sx={{ display: "inline-flex", opacity: 0.45, cursor: "not-allowed", "& *": { pointerEvents: "none" } }}>
        {children}
      </Box>
    </Tooltip>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return (
    <Typography component="span" variant="body2" sx={{ fontFamily: MONO }}>
      {children}
    </Typography>
  );
}

/** Local time, or an em dash. Every timestamp in the API is an ISO string in UTC. */
export function when(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString();
}

export function ago(value: string | null | undefined): string {
  if (!value) return "—";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "—";
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
